import {
  CancellationFeeAttribution,
  DEPOSIT_BOOKING_CONTEXTS,
  type IDepositForfeitureRule,
  type IDepositSettings,
} from '@principle-theorem/principle-core/interfaces';

/**
 * Every setting resolved except the flagged-patient deposit, which has no safe
 * default - inventing one would silently charge or skip a real amount.
 */
export type IResolvedDepositSettings = Required<
  Omit<IDepositSettings, 'patientDepositAmountCents'>
> &
  Pick<IDepositSettings, 'patientDepositAmountCents'>;

type ILegacyReadableDepositSettings = Omit<
  IDepositSettings,
  'forfeitureRules'
> & {
  forfeitureRules?: IDepositForfeitureRule[];
};

export const DEFAULT_CANCELLATION_POLICY_URL = '';
export const DEFAULT_AUTO_APPLY_APPOINTMENT_ACCOUNT_CREDITS = false;
export const DEFAULT_CANCELLATION_FEE_ATTRIBUTION =
  CancellationFeeAttribution.Practice;

/**
 * Owns how persisted deposit settings are read, so that documents written
 * before later fields existed resolve to the same shape everywhere and no
 * caller has to guess a default of its own.
 */
export class DepositSettings {
  /**
   * Read-only compatibility for deposit settings written before forfeiture rules
   * were persisted. This returns a new object and never writes legacy values.
   */
  static normalizeForRead(
    settings: ILegacyReadableDepositSettings
  ): IDepositSettings {
    return {
      ...settings,
      forfeitureRules:
        settings.forfeitureRules === undefined ? [] : settings.forfeitureRules,
    };
  }

  /**
   * Resolves every optional setting to a concrete value so callers can read
   * settings without repeating fallback logic that could drift between them.
   */
  static withDefaults(
    settings: ILegacyReadableDepositSettings
  ): IResolvedDepositSettings {
    const normalizedSettings = DepositSettings.normalizeForRead(settings);
    const resolvedSettings: IResolvedDepositSettings = {
      ...normalizedSettings,
      appliesTo: normalizedSettings.appliesTo?.length
        ? normalizedSettings.appliesTo
        : [...DEPOSIT_BOOKING_CONTEXTS],
      cancellationPolicyUrl:
        normalizedSettings.cancellationPolicyUrl ??
        DEFAULT_CANCELLATION_POLICY_URL,
      treatmentConfigurationDeposits:
        normalizedSettings.treatmentConfigurationDeposits ?? {},
      treatmentTemplateOverrides:
        normalizedSettings.treatmentTemplateOverrides ?? {},
      autoApplyAppointmentAccountCredits:
        normalizedSettings.autoApplyAppointmentAccountCredits ??
        DEFAULT_AUTO_APPLY_APPOINTMENT_ACCOUNT_CREDITS,
      cancellationFeeAttribution:
        normalizedSettings.cancellationFeeAttribution ??
        DEFAULT_CANCELLATION_FEE_ATTRIBUTION,
    };

    return resolvedSettings;
  }
}
